import React, { createContext, useContext, useEffect, useState } from 'react';
import { Session, User, AuthResponse, AuthTokenResponsePassword, AuthError } from '@supabase/supabase-js';
import { supabase } from './supabaseClient';
import { Database } from '../../types/supabase';

type Profile = Database['public']['Tables']['profiles']['Row'];
type ResetPasswordResponse = { data: {}; error: null } | { data: null; error: AuthError };

interface AuthContextType {
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<AuthTokenResponsePassword>;
  signUp: (email: string, password: string, fullName: string) => Promise<AuthResponse>;
  signOut: () => Promise<{ error: Error | null }>;
  resetPassword: (email: string) => Promise<ResetPasswordResponse>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Initialiser la session au chargement
    const fetchSession = async () => {
      const { data } = await supabase.auth.getSession();
      setSession(data.session);
      setUser(data.session?.user || null);
      
      if (data.session?.user) {
        fetchProfile(data.session.user.id);
      }
      
      setLoading(false);
    };

    fetchSession();

    // Abonnement aux changements d'authentification
    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        console.log(`Supabase auth event: ${event}`);
        setSession(newSession);
        setUser(newSession?.user || null);
        
        if (newSession?.user) {
          fetchProfile(newSession.user.id);
        } else {
          setProfile(null);
        }
        
        setLoading(false);
      }
    );

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);

  const fetchProfile = async (userId: string) => {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (error) {
      console.error('Erreur lors du chargement du profil:', error);
      
      // Si l'erreur est "aucune ligne trouvée", créer un profil
      if (error.code === 'PGRST116') {
        console.log('Profil non trouvé, création d\'un nouveau profil');
        
        // Récupérer les informations de l'utilisateur
        const { data: userData } = await supabase.auth.getUser();
        const user = userData?.user;
        
        if (user) {
          const { error: insertError } = await supabase
            .from('profiles')
            .insert({
              id: userId,
              email: user.email || '',
              full_name: user.user_metadata?.full_name || '',
              is_active: true // Nouveau compte = actif par défaut
            });
            
          if (insertError) {
            console.error('Erreur lors de la création du profil:', insertError);
            return;
          }
          
          // Récupérer le profil nouvellement créé
          const { data: newProfile } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', userId)
            .single();
            
          setProfile(newProfile);
          return;
        }
      }
      return;
    }

    // Vérifier si le compte est actif
    if (data && data.is_active === false) {
      console.log('Compte désactivé détecté, déconnexion automatique');
      
      // Déconnecter l'utilisateur
      await supabase.auth.signOut();
      
      // Nettoyer l'état local
      setSession(null);
      setUser(null);
      setProfile(null);
      
      // Rediriger vers la page de connexion avec un message
      window.location.replace('/login?message=compte_desactive');
      return;
    }

    setProfile(data);
  };

  const signIn = async (email: string, password: string) => {
    setLoading(true);
    const response = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);
    return response;
  };

  const signUp = async (email: string, password: string, fullName: string) => {
    setLoading(true);
    const response = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName,
        },
      },
    });

    // Si l'inscription est réussie, créer un profil
    if (response.data.user) {
      await supabase.from('profiles').insert({
        id: response.data.user.id,
        email,
        full_name: fullName,
      });
    }

    setLoading(false);
    return response;
  };

  const signOut = async () => {
    setLoading(true);
    const { error } = await supabase.auth.signOut();
    setLoading(false);
    return { error: error as Error | null };
  };

  const resetPassword = async (email: string) => {
    setLoading(true);
    const response = await supabase.auth.resetPasswordForEmail(email);
    setLoading(false);
    return response;
  };

  const value = {
    session,
    user,
    profile,
    loading,
    signIn,
    signUp,
    signOut,
    resetPassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth doit être utilisé à l\'intérieur d\'un AuthProvider');
  }
  return context;
}; 